import { AiSkillScope, SkillTemplate } from '@seen/core/models/skillTemplate'
import {
  Loader,
  PageLayout,
  SkillsList,
  toast,
  useCreateMutation,
  useCurrentPractitionerPerms,
  useIsReadOnly,
  usePatchMutation,
  useSearch,
  useVisibleSkills,
} from '@seen/react'
import { Button } from '@seen/ui'
import { IconPlus, IconSparkles } from '@tabler/icons-react'
import type { JSX } from 'react'
import { useNavigate } from 'react-router'

export function AiSkillsPage(): JSX.Element {
  const navigate = useNavigate()
  const { isTech } = useCurrentPractitionerPerms()
  const isReadOnly = useIsReadOnly()

  const { data: skills, isError } = useSearch(
    SkillTemplate,
    SkillTemplate.searchQuery()
  )

  const createMutation = useCreateMutation<SkillTemplate>()
  const patchMutation = usePatchMutation<SkillTemplate>()

  const visibleSkills = useVisibleSkills(skills)

  if (isError) {
    return (
      <PageLayout title="AI Skills">
        <div className="text-sm text-muted-foreground">
          Failed to load AI skills.
        </div>
      </PageLayout>
    )
  }

  if (!skills) {
    return <Loader />
  }

  return (
    <PageLayout
      title="AI Skills"
      actions={
        !isReadOnly ? (
          <Button
            type="button"
            variant="outline"
            className="gap-2"
            disabled={createMutation.isPending}
            onClick={() => {
              const skill = new SkillTemplate({
                title: 'Untitled skill',
                description: '',
              })
              skill.content = ''
              skill.scope = AiSkillScope.Personal
              createMutation.mutate(skill, {
                onSuccess: (skill) => {
                  void navigate(`/settings/skills/${skill.id}`)
                },
                onError: () => {
                  toast.error('Failed to create AI skill')
                },
              })
            }}
          >
            <IconPlus className="size-4" />
            New skill
          </Button>
        ) : undefined
      }
    >
      <div className="flex max-w-5xl flex-col gap-4">
        <div className="flex items-start gap-3 rounded-md border bg-muted/20 p-4">
          <IconSparkles className="mt-0.5 size-5 shrink-0 text-primary" />
          <div className="flex flex-col gap-1">
            <p className="text-sm font-medium">Reusable Orbit AI behavior</p>
            <p className="text-sm text-muted-foreground">
              Skills are reusable instructions that can be attached to an AI
              chat request. Personal skills are visible to you; workspace skills
              are visible to everyone.
              {isTech &&
                ' As a member of the tech team, you can see, trigger, and manage everyone’s skills.'}
            </p>
          </div>
        </div>
        <SkillsList
          skills={visibleSkills}
          onSkillClick={(skill) => navigate(`/settings/skills/${skill.id}`)}
          onDeleteSkill={
            isReadOnly
              ? undefined
              : (skill) => {
                  patchMutation.mutate(
                    {
                      instance: skill,
                      mutateFn: (draft) => {
                        draft.status = 'retired'
                      },
                    },
                    {
                      onError: () => {
                        toast.error('Failed to delete AI skill')
                      },
                    }
                  )
                }
          }
        />
      </div>
    </PageLayout>
  )
}
